import { CirclePauseIcon, DumbbellIcon, ExpediaImage, LockIcon, VoucherIcon, WriteIcon } from "@xola/icons";
import { Badge, Dot } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import { DemographicIcon, getWaiverParticipantCount, isEquipmentItem, PurchaseItemStatus } from "common";
import { isEmpty } from "lodash";
import React from "react";
import { Experience, ExperiencePurchaseItem, LineItemType, Purchase, PurchaseItemPrivacy } from "types";
import { NoTranslate } from "../../Common/NoTranslate";
import { CheckInQuickSearchGuestMenu } from "../../PurchaseModification/CheckInPurchaseGuests/CheckInQuickSearchMenu";
import { NotesBadge } from "../Common/NotesBadge";
import { PurchaseBalanceBadge } from "../Common/PurchaseBalanceBadge";
import { PurchaseActionMenu } from "../PurchaseActionMenu";
import { DashboardSearchItemTime } from "../Time";
import { useResource } from "@/hooks/useResource";
import { getDefaultPhotoUrl } from "@/models/Product";
import { getCustomerName } from "@/models/Purchase";
import { getShortCode } from "@/models/PurchaseItem";
import { getAffiliateTicketCode, getOTATicketCode } from "@/models/Ticket";
import { canViewWaivers } from "@/stores/globalStore";

interface DashboardSearchItemProps {
    className?: string;
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isActive: boolean;
    onPurchaseNotesClick: (purchase: Purchase, item: ExperiencePurchaseItem) => void;
    onMenuOpenChange: (isOpen: boolean, item: ExperiencePurchaseItem) => void;
}

export const DashboardSearchItem = ({
    purchase,
    item,
    isActive,
    className,
    onPurchaseNotesClick,
    onMenuOpenChange,
}: DashboardSearchItemProps) => {
    const { data: experience } = useResource<Experience>(["products", item.product.id]);
    // Equipment without collect-demographics has no attendee guests but is still check-in eligible at the
    // item level (organizer). The menu itself hides when check-in is disabled. XS-1821.
    const isCheckInEligible = (item.guests && item.guests.length > 0) || isEquipmentItem(item);

    const noteCount = purchase.notes?.length ?? 0;
    const customerName = getCustomerName(purchase);

    const waiverParticipantCount = getWaiverParticipantCount(item.waivers);
    let waiverDotColor = "danger";
    if (waiverParticipantCount > 0) {
        waiverDotColor = waiverParticipantCount >= item.quantity ? "success" : "warning";
    }

    const handleMenuOpenChange = useMemoizedFn((isOpen: boolean) => onMenuOpenChange(isOpen, item));

    const otaTicketCode = item.tickets ? getOTATicketCode(item.tickets) : "";
    const affiliateTicketCode = item.tickets ? getAffiliateTicketCode(item.tickets) : "";

    return (
        <div
            className={clsx(
                "ui-search-result-item",
                "flex cursor-pointer p-4",
                isActive && "bg-gray-lighter",
                className,
            )}
        >
            <DashboardSearchItemTime
                showSellerTimezone={false}
                time={item.arrivalTime}
                dateTime={item.arrivalDateTime}
                className="mr-2.5"
            />

            <div className="flex-1">
                <div className="mb-2.5 flex">
                    <img
                        className="mr-2.5 h-12 w-12 rounded border border-gray-light bg-gray-lighter object-cover"
                        src={getDefaultPhotoUrl(item.product, "small")}
                    />
                    <div className="pt-1">
                        <NoTranslate className="font-semibold" data-testid="dashboard-search-item-customer-name">
                            {isEmpty(customerName) ? getShortCode(item)?.toUpperCase() : customerName}
                        </NoTranslate>
                        <NoTranslate as="div" className="text-sm" data-testid="dashboard-search-item-experience-name">
                            {experience?.name}
                        </NoTranslate>
                    </div>
                </div>

                <div className="mb-1 flex items-center space-x-3 text-gray-darker">
                    {item.privacy === PurchaseItemPrivacy.PRIVATE ? (
                        <Badge color="warning" className="ui-dashboard-search-item-private">
                            <LockIcon />
                        </Badge>
                    ) : null}

                    <span className="space-x-3" data-testid="dashboard-search-item-demographic-count">
                        <DemographicIcon name="Adults" /> <NoTranslate>{getDemographicItemsCount(item)}</NoTranslate>
                        {item.totalGuestWeight?.value ? (
                            <span className="mt-0.5 space-x-0.5">
                                <DumbbellIcon />
                                <NoTranslate>
                                    {item.totalGuestWeight.value} {item.totalGuestWeight.unit}
                                </NoTranslate>
                            </span>
                        ) : null}
                    </span>
                </div>

                <div
                    className="flex w-full items-center space-x-3 text-gray-darker"
                    data-testid="dashboard-search-item-waiver-count"
                >
                    {canViewWaivers() && (
                        <span className="space-x-0.5">
                            <Dot className="mb-1" color={waiverDotColor} />
                            <WriteIcon />
                            <span>
                                {waiverParticipantCount}/{item?.quantity}
                            </span>
                        </span>
                    )}

                    {otaTicketCode && (
                        <span>
                            <ExpediaImage className="mt-0.5" size="medium" />
                            <NoTranslate>{otaTicketCode}</NoTranslate>
                        </span>
                    )}

                    {affiliateTicketCode && (
                        <span>
                            <VoucherIcon size="medium" />
                            <NoTranslate>{affiliateTicketCode}</NoTranslate>
                        </span>
                    )}
                </div>
            </div>

            <div>
                <div className="mb-6 text-right" onClick={(e) => e.stopPropagation()}>
                    {isCheckInEligible ? (
                        <div className="mr-5 inline">
                            <CheckInQuickSearchGuestMenu
                                purchase={purchase}
                                purchaseItem={item}
                                onOpenChange={handleMenuOpenChange}
                            />
                        </div>
                    ) : null}

                    <PurchaseActionMenu purchase={purchase} item={item} onOpenChange={handleMenuOpenChange} />
                </div>

                <div className="flex items-center space-x-2">
                    <PurchaseBalanceBadge
                        purchase={purchase}
                        item={item}
                        className={clsx(purchase.balance > 0 || "invisible")}
                        onOpenChange={handleMenuOpenChange}
                    />

                    {(item.status === PurchaseItemStatus.PENDING_ACCEPT ||
                        item.status === PurchaseItemStatus.PENDING_MINIMUM) && (
                        <Badge color="warning" icon={<CirclePauseIcon className="text-warning" />}>
                            Pending
                        </Badge>
                    )}

                    <NotesBadge
                        noteCount={noteCount}
                        className="font-normal"
                        onClick={(e: any) => {
                            onPurchaseNotesClick(purchase, item);
                            e.stopPropagation();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const getDemographicItemsCount = (item: ExperiencePurchaseItem) => {
    return item.lineItems
        .filter((item) => item.type === LineItemType.DEMOGRAPHIC && !!item.quantity)
        .reduce((sum, lineItem) => sum + (lineItem?.quantity ?? 0), 0);
};
